/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The state of the evaporator.
 */
export const EvaporatorState = {
  Cooling: "cooling",
  Satisfied: "satisfied",
  Defrost: "defrost",
  Off: "off",
  Faulted: "faulted",
  Estopped: "estopped",
} as const;
/**
 * The state of the evaporator.
 */
export type EvaporatorState = OpenEnum<typeof EvaporatorState>;

/**
 * The configuration of the animatic for an evaporator.
 */
export type AnimaticEvaporatorConfig = {
  /**
   * References for the current duration of the defrost process. Used to compute the percent of defrost completed.
   */
  currentDurationRefs?: Array<Ref> | undefined;
  /**
   * Whether the defrost is complete.
   */
  defrostComplete?: boolean | undefined;
  /**
   * References for the location of the ticks on the defrost progress bar. NOTE: there is 1 less tick than the number of stages.
   */
  defrostTickRefs?: Array<Ref> | undefined;
  /**
   * The state of the evaporator.
   */
  evaporatorState: EvaporatorState;
  /**
   * A reference to a reading or setting.
   */
  fanStateRef?: Ref | undefined;
  /**
   * A reference to a reading or setting.
   */
  totalDefrostDurationRef?: Ref | undefined;
};

/** @internal */
export const EvaporatorState$inboundSchema: z.ZodType<
  EvaporatorState,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(EvaporatorState);
/** @internal */
export const EvaporatorState$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  EvaporatorState
> = openEnums.outboundSchema(EvaporatorState);

/** @internal */
export const AnimaticEvaporatorConfig$inboundSchema: z.ZodType<
  AnimaticEvaporatorConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  current_duration_refs: types.optional(z.array(Ref$inboundSchema)),
  defrost_complete: types.boolean().default(false),
  defrost_tick_refs: types.optional(z.array(Ref$inboundSchema)),
  evaporator_state: EvaporatorState$inboundSchema,
  fan_state_ref: types.optional(Ref$inboundSchema),
  total_defrost_duration_ref: types.optional(Ref$inboundSchema),
}).transform((v) => {
  return remap$(v, {
    "current_duration_refs": "currentDurationRefs",
    "defrost_complete": "defrostComplete",
    "defrost_tick_refs": "defrostTickRefs",
    "evaporator_state": "evaporatorState",
    "fan_state_ref": "fanStateRef",
    "total_defrost_duration_ref": "totalDefrostDurationRef",
  });
});
/** @internal */
export type AnimaticEvaporatorConfig$Outbound = {
  current_duration_refs?: Array<Ref$Outbound> | undefined;
  defrost_complete: boolean;
  defrost_tick_refs?: Array<Ref$Outbound> | undefined;
  evaporator_state: string;
  fan_state_ref?: Ref$Outbound | undefined;
  total_defrost_duration_ref?: Ref$Outbound | undefined;
};

/** @internal */
export const AnimaticEvaporatorConfig$outboundSchema: z.ZodType<
  AnimaticEvaporatorConfig$Outbound,
  z.ZodTypeDef,
  AnimaticEvaporatorConfig
> = z.object({
  currentDurationRefs: z.array(Ref$outboundSchema).optional(),
  defrostComplete: z.boolean().default(false),
  defrostTickRefs: z.array(Ref$outboundSchema).optional(),
  evaporatorState: EvaporatorState$outboundSchema,
  fanStateRef: Ref$outboundSchema.optional(),
  totalDefrostDurationRef: Ref$outboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    currentDurationRefs: "current_duration_refs",
    defrostComplete: "defrost_complete",
    defrostTickRefs: "defrost_tick_refs",
    evaporatorState: "evaporator_state",
    fanStateRef: "fan_state_ref",
    totalDefrostDurationRef: "total_defrost_duration_ref",
  });
});

export function animaticEvaporatorConfigToJSON(
  animaticEvaporatorConfig: AnimaticEvaporatorConfig,
): string {
  return JSON.stringify(
    AnimaticEvaporatorConfig$outboundSchema.parse(animaticEvaporatorConfig),
  );
}
export function animaticEvaporatorConfigFromJSON(
  jsonString: string,
): SafeParseResult<AnimaticEvaporatorConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AnimaticEvaporatorConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AnimaticEvaporatorConfig' from JSON`,
  );
}
